import {
	CognitoUserPool,
	CognitoUser,
	CognitoUserSession,
	CognitoAccessToken,
	CognitoIdToken,
	CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import * as AWSSDK from 'aws-sdk';
import emailMask from 'email-mask';
import {
	AuthFlowType,
	CognitoIdentityProviderClient,
	LimitExceededException,
	NotAuthorizedException,
	PasswordResetRequiredException,
	UserNotConfirmedException,
	UserNotFoundException,
} from '@aws-sdk/client-cognito-identity-provider';
import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, tap } from 'rxjs';
import { ENV, Environment } from '../../config/environment';
import { AppUtil } from '../../utils/app.util';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { Config } from '../../config/config';
import { DataCarrierService } from './data-carrier.service';
import { UTMParameters } from '../../beans/auth/utm-parameters.bean';
import { UtmParams } from '../../modules/scheduling/types.scheduling';
import { JwtHelper } from './jwt-helper.service';
import { APP_USER_PERMISSIONS, PERMISSON_ALLOWED_KEY } from 'src/app/constants/app-user-permissions.constants';
import { NgxPermissionsService } from 'ngx-permissions';
import { MfaService } from './mfa.service';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { AUTH_COMPONENTS } from 'src/app/constants/scheduling.constants';
import { ModalService } from './modal.service';
import { MfaSetupState, MfaUiCommands } from 'src/app/modules/shared/types';
import { Router } from '@angular/router';
import { signIn, fetchAuthSession, confirmSignIn, AuthSession, signOut } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { ClientMetadata, ConfirmSignInOutput } from '@aws-amplify/auth/dist/esm/providers/cognito/types';
import { CredentialsOptions } from 'aws-sdk/lib/credentials';

type ConfirmSignInOutputWithError = ConfirmSignInOutput & { error?: any };
/** AWS Object */
declare let AWS: any;
/** Cognito pool data object */
let PoolData = {
	ClientId: '',
	UserPoolId: '',
	IdentityPoolId: '',
};
/** hold user pool object */
let userPool: CognitoUserPool;

/**
 * Cognito related authentication services
 */
@Injectable({
	providedIn: 'root',
})
export class AuthService {
	env: ENV;
	/** Observable returning the status cognito operations */
	result: Subject<any>;
	signUpResult: Subject<any>;
	response: any;
	cognitoUser: CognitoUser;
	loginSessionTimeout: NodeJS.Timeout | string | number | undefined;
	loginFlowSubscriptions: Subscription = new Subscription();
	mfaFlowSubscriptions: Subscription = new Subscription();
	mfaCodePromptSubscription: Subscription = new Subscription();
	private currentUserSessionPromise: Promise<AuthSession>;
	private currentUserSession: AuthSession;
	private cognitoClient: CognitoIdentityProviderClient;
	/**
	 * Auth Service constructor
	 * @param environment - Environment Config
	 * @param config - URL Config
	 * @param dataCarrier - Data Carrier Service
	 * @param router - Angular Router
	 */
	constructor(
		private readonly environment: Environment,
		private readonly config: Config,
		private readonly dataCarrier: DataCarrierService,
		private readonly jwtHelper: JwtHelper,
		private readonly permissionService: NgxPermissionsService,
		private router: Router,
		private readonly mfaService: MfaService,
		private readonly spinner: SpinnerComponent,
		private readonly modalService: ModalService,
	) {
		this.env = this.environment.getEnvironment();
		/** constricting cognito pool data using cognito credentials */
		PoolData = {
			ClientId: this.env.CLIENT_ID,
			UserPoolId: this.env.USER_POOL_ID,
			IdentityPoolId: this.env.IDENTITY_POOL_ID,
		};
		userPool = new CognitoUserPool(PoolData);
		this.result = new Subject<any>();
		this.signUpResult = new Subject<any>();
		this.configureAmplify();
	}

	configureAmplify(): void {
		Amplify.configure({
			...Amplify.getConfig(),
			auth: {
				aws_region: this.env.AWS_REGION,
				user_pool_client_id: PoolData.ClientId,
				user_pool_id: PoolData.UserPoolId,
				identity_pool_id: PoolData.IdentityPoolId,
				unauthenticated_identities_enabled: true,
			},
			Auth: {
				Cognito: {
					userPoolId: PoolData.UserPoolId,
					userPoolClientId: PoolData.ClientId,
					identityPoolId: PoolData.IdentityPoolId,
					allowGuestAccess: true,
					loginWith: {
						email: true,
					},
				},
			},
		});
		this.updateAwsCredentials();
	}

	async configureCognitoClient() {
		try {
			const env: any = this.environment.getEnvironment();
			this.cognitoClient = new CognitoIdentityProviderClient({
				region: env.AWS_REGION,
				credentials: (await this.getCurrentAmplifyUserAuthSession()).credentials,
			});
			return this.cognitoClient;
		} catch (error) {
			console.error('Error configuring Cognito client:', error);
			throw new Error('Failed to configure Cognito client');
		}
	}

	/**
	 * Getter method for IDToken
	 */
	getToken() {
		let jwtToken = null;
		if (sessionStorage.getItem(this.config.TOKEN_NAME)) {
			const authToken = JSON.parse(sessionStorage.getItem(this.config.TOKEN_NAME));
			jwtToken = authToken.idToken.jwtToken;
		}
		return jwtToken;
	}

	loadAppLevelPermissions() {
		const applicationPermissionsForLoggedInUser = this.getAppPermissionsForLoggedInUser();
		if (applicationPermissionsForLoggedInUser) {
			applicationPermissionsForLoggedInUser.forEach(permission => {
				this.permissionService.addPermission(permission);
			});
		}
		// console.log('permissions available are ', this.permissionService.getPermissions());
	}

	getAppPermissionsForLoggedInUser() {
		const decodedToken = this.jwtHelper.getDecodedToken();
		if (decodedToken && decodedToken[PERMISSON_ALLOWED_KEY]) {
			const permissionsStringArray = decodedToken ? decodedToken[PERMISSON_ALLOWED_KEY].split(',') : null;
			if (permissionsStringArray) {
				// parse string into enumerables thus only allowing permissions which are mutually decided both on FE and BE
				const permissions = permissionsStringArray
					.map((permission: string) => {
						if (permission in APP_USER_PERMISSIONS) {
							return APP_USER_PERMISSIONS[permission];
						}
						return null;
					})
					.filter(p => p);
				return permissions;
			}
		}
		return null;
	}

	async hasPermission(permission: string): Promise<boolean> {
		const permissions = this.permissionService.getPermissions();
		return !!permissions[permission];
	}

	/**
	 * Getter method for Token expiry date
	 * @param token IDToken
	 */
	getTokenExpirationDate(token: string): Date {
		const decoded = jwtDecode<JwtPayload>(token);
		if (decoded.exp === undefined) {
			return null;
		}
		const date = new Date(decoded.exp);
		return date;
	}

	/**
	 * checking whether the given token expired or not
	 * by checking expiry timestamp of token
	 * @param token - IDToken
	 * return true or false
	 */
	isTokenExpired(token?: string): boolean {
		if (!token) {
			token = this.getToken();
		}
		if (!token) {
			return true;
		}
		const date = this.getTokenExpirationDate(token);
		if (date === undefined) {
			return false;
		}
		// Rounding off current timestamp to 10 digits
		const currentTimestamp = Math.floor(new Date().valueOf() / 1000);
		return !(date.valueOf() > currentTimestamp);
	}

	/**
	 * Cognito Sign up
	 * @param object - contain user related information like email and password
	 * return the response using Observable
	 */
	signUpUser(object) {
		this.signUpResult = new Subject<any>();
		userPool.signUp(AppUtil.getFormattedEmail(object.email), object.password, null, null, (err, res) => {
			if (err) {
				console.error(`[${AuthService.name}][${this.signUpUser.name}]`, 'Error during user signup', object.email, err);
				const error = { success: false, object: err };
				this.signUpResult.error(error);
			} else {
				this.signUpResult.next({ success: true, object: '' });
			}
		});
	}

	private clearStorageAndCredentials() {
		/**
		 * clear local storage, session storage and  AWS Credentials
		 * before new session begins
		 */
		// Getting utm params which is set before login, and setting back to local storage
		const utm = localStorage.getItem('utm');
		const forgotPassToaster = sessionStorage.getItem('forgotPassToaster');
		const createAccountToaster = sessionStorage.getItem('createAccountToaster');
		const globalMfaStatus = this.mfaService.ss_getGlobalMfaStatus();
		const mfaActivatedAt = this.mfaService.ss_getMfaActivatedAt();

		// try { signOut({ global: true }); } catch (error) { }
		localStorage.clear();
		sessionStorage.clear();
		this.currentUserSession = undefined;
		this.currentUserSessionPromise = undefined;

		// Setting back the params in local storage
		localStorage.setItem('utm', utm);
		sessionStorage.setItem('forgotPassToaster', forgotPassToaster);
		sessionStorage.setItem('createAccountToaster', createAccountToaster);
		this.mfaService.ss_setGlobalMfaStatus(globalMfaStatus);
		this.mfaService.ss_setMfaActivatedAt(mfaActivatedAt);

		if (AWS.config.credentials) {
			AWS.config.credentials.clearCachedId();
		}
	}

	private handleLoginError(error: any, email: string) {
		// Forcing reset password, when email is changed on epms side
		console.info('Auth.service.ts -> handleLoginError(error):', error);
		this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.HIDE);

		if (error instanceof NotAuthorizedException) {
			console.log('instanceof NotAuthorizedException');
			this.result.next({
				error: {
					message: 'Invalid username or password',
					code: '400',
					name: 'Invalid username or password',
				}
			});
		} else if (error instanceof UserNotFoundException) {
			console.log('instanceof UserNotFoundException');
			this.result.next({
				error: {
					message: 'User not found',
					code: '404',
					name: 'UserNotFoundException',
				}
			});
		} else if (error instanceof UserNotConfirmedException) {
			console.log('instanceof UserNotConfirmedException');
			this.result.next({
				error: {
					message: 'User is not confirmed',
					code: '403',
					name: 'UserNotConfirmedException',
				}
			});
		} else if (error instanceof PasswordResetRequiredException) {
			console.log('instanceof PasswordResetRequiredException');
			this.router.navigate(['/reset/password'], {
				queryParams: { email: btoa(email), resetPassword: true },
			});
			this.result.next({
				error: {
					message: 'Password reset is required',
					code: '401',
					name: 'PasswordResetRequiredException',
				}
			});
		} else if (error instanceof LimitExceededException) {
			console.log('instanceof LimitExceededException');
			this.result.next({
				error: {
					message: 'Maximum attempt exceeded',
					code: '400',
					name: 'LimitExceededException',
				}
			});
		} else {
			console.log('Unknown Exception in [handleLoginError]');
			this.result.next({ error });
		}
		this.spinner.stopLoader();
	}

	private clearLoginFlowSubscriptions() {
		console.info(`Clearing Login Flow Subscriptions...`, this.loginFlowSubscriptions);
		this.loginFlowSubscriptions.unsubscribe();
		this.mfaFlowSubscriptions.unsubscribe();
		this.clearMFAFlowSubscriptions();
	}

	private clearMFAFlowSubscriptions() {
		console.info(`Clearing MFA Flow Subscriptions...`, this.mfaFlowSubscriptions, this.mfaCodePromptSubscription);
		this.mfaCodePromptSubscription.unsubscribe();
		this.mfaFlowSubscriptions.unsubscribe();
	}

	/**
	 * Cognito Login
	 * @param email - user email Id
	 * @param password - user password
	 */
	async login(email: string, password: string, authMetadata: string = AUTH_COMPONENTS.LOGIN_COMPONENT) {
		if (this.loginSessionTimeout) {
			console.info('LOGIN ALREADY IN PROGRESS');
			return;
		}

		// Clear storage and AWS credentials
		this.clearStorageAndCredentials();

		this.loginFlowSubscriptions = new Subscription();

		try {
			// await this.configureCognitoClient();
			const clientMetadata: ClientMetadata = {
				loginSource: authMetadata,
			};

			this.loginFlowSubscriptions.add(
				// MFA Input Form Closing
				this.mfaService.displayMfaInputScreen.subscribe(showMfaInputScreen => {
					console.info('displayMfaInputScreen.showMfaInputScreen?: ', showMfaInputScreen);
					if (showMfaInputScreen === MfaUiCommands.HIDE && !this.jwtHelper.getDecodedToken()) {
						this.clearLoginSessionTimeout();
						this.clearLoginFlowSubscriptions();
					}
				}),
			);
			this.loginFlowSubscriptions.add(
				// Send/Resend MFA Code
				this.mfaService.resendMfaCodeObserver.subscribe(async resendMfaCode => {
					console.info('resendMfaCodeObserver.resendMfaCode?: ', resendMfaCode);
					if (resendMfaCode) {
						await this.loginWithMfa(email, password, PoolData.ClientId, clientMetadata);
					}
				})
			);

			// Start Login Process
			this.mfaService.resendMfaCode(true);

		} catch (error) {
			console.error('Login error:', error);
			this.clearLoginSessionTimeout();
			this.clearLoginFlowSubscriptions();
			// this.handleLoginError(error, email);
		}
	}

	private async loginWithMfa(email: string, password: string, clientId: string, clientMetadata: ClientMetadata) {
		try {
			this.clearLoginSessionTimeout();
			this.cognitoUser = new CognitoUser({
				Username: email,
				Pool: userPool,
			});

			console.log('Amplify:', Amplify.getConfig());

			// https://docs.amplify.aws/angular/build-a-backend/auth/connect-your-frontend/sign-in/
			const singInRes = await signIn({ username: email, password, options: { authFlowType: AuthFlowType.USER_PASSWORD_AUTH, clientMetadata } });
			console.log('singInRes:', singInRes);

			if (singInRes.nextStep.signInStep === 'DONE') {
				await this.cognitoSessionUpdate();
				this.clearLoginSessionTimeout();
				this.clearLoginFlowSubscriptions();
			} else if (
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
			) {
				this.clearMFAFlowSubscriptions();
				this.mfaFlowSubscriptions = new Subscription();
				// collect OTP from user
				const subscription = this.handleEmailOtpResponse(email).subscribe(cognitoUserSession => {
					console.info('CognitoUserSession:', cognitoUserSession);
					if (!cognitoUserSession) {
						return;
					}
					const idToken = cognitoUserSession?.getIdToken().payload;
					if (idToken) {
						console.info('CognitoUserSession Received');

						this.mfaService.ss_setUserMfaStatus(JSON.parse(idToken['custom:mfa-enabled']));
						console.info(`idToken['custom:mfa-enabled']:`, idToken['custom:mfa-enabled']);

						const mfaDisabledReason = idToken['custom:mfa-disable-reason'];
						console.info(`idToken['custom:mfa-disable-reason']:`, mfaDisabledReason);

						if (mfaDisabledReason) {
							this.mfaService.ss_setMfaDisabledReason(mfaDisabledReason);
						}
						this.mfaService.fetchGlobalMfaStatus();
						this.mfaService.displayMfaErrorMessage(false, '');

						this.clearLoginSessionTimeout();
						this.clearLoginFlowSubscriptions();
					} else {
						console.info('CognitoUserSession NOT Received');
					}
					subscription.unsubscribe();
				});
				this.mfaFlowSubscriptions.add(subscription);
			} else if (singInRes.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION") {
				// present nextStep.allowedMFATypes to user
				// collect user selection
				// await confirmSignIn({
				// 	challengeResponse: "EMAIL", // 'EMAIL', 'SMS', or 'TOTP'
				// });
			} else if (singInRes.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION") {
				// present nextStep.allowedMFATypes to user
				// collect user selection
				// await confirmSignIn({
				// 	challengeResponse: "EMAIL", // 'EMAIL' or 'TOTP'
				// });
			} else if (singInRes.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP") {
				// collect email address from user
				// await confirmSignIn({
				// 	challengeResponse: "hello@mycompany.com",
				// });
			} else if (singInRes.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
				// present nextStep.totpSetupDetails.getSetupUri() to user
				// collect OTP from user
				// await confirmSignIn({
				// 	challengeResponse: "123456",
				// });
			}
		} catch (error) {
			console.error('Auth Init Command - Error', error);
			this.clearLoginSessionTimeout();
			this.clearLoginFlowSubscriptions();
			this.handleLoginError(error, email);
		}
	}

	private handleEmailOtpResponse(email: string): Observable<CognitoUserSession> {
		console.info('EMAIL-OTP Flow:');
		this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.SHOW);
		const maskedEmail = emailMask(email, '*')?.replace(/\*+/, '***')?.replace(/(@[A-z]).*([A-z])/, '$1***$2');
		setTimeout(() => this.mfaService.setCodeDeliveryDestination(maskedEmail), 0);
		this.spinner.stopLoader(); // Stop the loading spinner

		const subject = new Subject<CognitoUserSession>();

		this.mfaCodePromptSubscription = this.mfaService.mfaCode.subscribe(async mfaCode => {
			console.info('MFA -> mfaCode:', mfaCode, mfaCode?.trim(), mfaCode?.trim()?.length);

			mfaCode = mfaCode?.trim();
			if (!mfaCode?.length) {
				return
			}
			this.spinner.startLoader();
			this.clearLoginSessionTimeout();
			let mfaVerifyResponse: ConfirmSignInOutputWithError;
			try {
				// mfaVerifyResponse = await confirmSignIn({ challengeResponse: mfaCode })
				mfaVerifyResponse = await this.completeMFAChallenge(mfaCode);
				console.info('MFA verification Response:', mfaVerifyResponse);
				if (mfaVerifyResponse?.isSignedIn) {
					console.info('MFA verification success:', mfaVerifyResponse);

					const isSuccessScreenOpen = this.isShowingMfaSuccessScreen();
					console.log('isShowingMfaSuccessScreen:', isSuccessScreenOpen);

					if (isSuccessScreenOpen) {
						const mfaSuccessScreenSubscription = isSuccessScreenOpen.subscribe(async successScreenClosed => {
							console.log('successScreenClosed:', successScreenClosed);
							if (successScreenClosed) {
								const cognitoUserSession = await this.cognitoSessionUpdate();
								mfaSuccessScreenSubscription.unsubscribe();
								console.log('sending cognitoUserSession:', cognitoUserSession);
								subject.next(cognitoUserSession);
								subject.complete();
							}
						});
						this.mfaFlowSubscriptions.add(mfaSuccessScreenSubscription);
						this.mfaCodePromptSubscription.unsubscribe();
					} else {
						const cognitoUserSession = await this.cognitoSessionUpdate();
						this.mfaCodePromptSubscription.unsubscribe();
						console.log('sending cognitoUserSession:', cognitoUserSession);
						subject.next(cognitoUserSession);
						subject.complete();
					}
				} else {
					console.info('Invalid mfaVerifyResponse:', mfaVerifyResponse);
					if (mfaVerifyResponse?.error?.name === 'CodeMismatchException') {
						console.info('CodeMismatchException, allow  retyping');
						// When code mismatch, allow retyping
					} else {
						this.mfaCodePromptSubscription.unsubscribe();
						subject.next(undefined);
						subject.complete();
					}
				}
			} catch (error) {
				console.error('Error handleEmailOtpResponse():', error);
				this.mfaCodePromptSubscription.unsubscribe();
				subject.next(undefined);
				subject.complete();
				this.spinner.stopLoader();
				// mfaCommandCode = 'CONTINUE';
			}
		});
		// this.mfaFlowSubscriptions.add(mfaCodePromptSubscription);
		return subject.asObservable();
	}

	private async cognitoSessionUpdate() {
		// this.result = new Subject<any>();
		const authSession = await this.getCurrentAmplifyUserAuthSession();
		const cognitoUserSession = new CognitoUserSession({
			AccessToken: new CognitoAccessToken({ AccessToken: authSession?.tokens?.accessToken?.toString() }),
			IdToken: new CognitoIdToken({ IdToken: authSession?.tokens?.idToken?.toString() }),
			RefreshToken: new CognitoRefreshToken({ RefreshToken: undefined }),
		});
		console.info('cognitoUserSession:', cognitoUserSession);

		sessionStorage.setItem(this.config.TOKEN_NAME, JSON.stringify(cognitoUserSession));
		sessionStorage.setItem('userId', authSession?.tokens?.idToken?.payload?.sub);
		this.cognitoUser.setSignInUserSession(cognitoUserSession);
		this.updateAwsCredentials(authSession);
		this.result.next({ data: cognitoUserSession });
		return cognitoUserSession;
	}

	/**
	 * Show MFA Success screen if needed and return an observer to handle it's dismiss OTHERWISE return false
	 * @returns `false` if user has already seen MFA success screen, (based on their MFA Setup State, `FIRST_TIME` or `ALREADY_SET`) \
	 * Otherwise an `Observable` which sends `true` when MFA Success screen is closed. \
	 * Observer is auto completed after received once.
	 */
	private isShowingMfaSuccessScreen() {
		// this.spinner.stopLoader();
		const mfaActivatedAt = this.mfaService.ss_getMfaActivatedAt();
		console.info('MFA -> ss_getMfaActivatedAt:', mfaActivatedAt);

		if (!mfaActivatedAt || mfaActivatedAt === MfaSetupState.UNDEFINED) {
			console.info('MFA -> MFA Success Screen - Showing');
			this.spinner.stopLoader();
			this.mfaService.setMfaActivatedAt(MfaSetupState.FIRST_TIME);
			this.mfaService.ss_setMfaActivatedAt(MfaSetupState.FIRST_TIME);

			const subject = new Subject<true>();

			const subscription = this.mfaService.displayMfaSuccessScreen.subscribe(uiCommand => {
				if (uiCommand === MfaUiCommands.HIDE) {
					console.info('MFA -> MFA Success Screen - Dismissed');
					subscription.unsubscribe();
					this.spinner.startLoader();
					this.mfaService.toggleMfaSuccessScreen(MfaUiCommands.HIDE);
					subject.next(true)
					subject.complete();
				}
			});
			this.loginFlowSubscriptions.add(subscription);
			return subject.asObservable();
		} else {
			return false;
		}
	}

	private async completeMFAChallenge(code: string): Promise<ConfirmSignInOutputWithError> {
		try {
			this.spinner.startLoader();
			const response = await confirmSignIn({ challengeResponse: code });
			return response; // Return success if no error occurs
		} catch (error) {
			console.log('confirmSignIn Error:', error);
			this.spinner.stopLoader();
			if (error?.name) {
				console.log('confirmSignIn Error:', JSON.stringify(error));
				if (error.name === 'NotAuthorizedException') {
					this.handleLoginSessionExpiredForMfa();
				} else if (error.name === 'CodeMismatchException') {
					this.mfaService.displayMfaErrorMessage(
						true,
						'The code you entered is incorrect. Please re-enter the code to try again.'
					);
				} else if (error.name === 'ExpiredCodeException') {
					this.mfaService.displayMfaErrorMessage(true, 'MFA code has expired');
				} else if (error.name === 'LimitExceededException') {
					this.mfaService.displayMfaErrorMessage(true, 'Limit Exceeded');
				}
			} else {
				console.error('An unexpected error occurred during MFA verification:', error);
				this.mfaService.displayMfaErrorMessage(true, 'An unexpected error occurred during MFA verification');
			}
			return { isSignedIn: false, nextStep: undefined, error };
		}
	}

	private clearLoginSessionTimeout(): void {
		if (this.loginSessionTimeout) {
			clearInterval(this.loginSessionTimeout);
			console.info('MFA INPUT -> Login Session - Cleared:', this.loginSessionTimeout, `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`);
			this.loginSessionTimeout = undefined;
		}
	}

	private handleLoginSessionExpiredForMfa(): void {
		console.info('MFA INPUT -> Login Session Expired:', `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`);
		this.modalService.openSessionExpireModal(
			'LOGIN SESSION EXPIRED',
			`Your login session has expired, please try again and submit OTP within 3 minutes!`,
			() => {
				this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.HIDE);
				const queryParams = { queryParams: { tab: 'login' } };
				this.router.navigate(['/login'], queryParams);
			}
		);
	}

	getCurrentAmplifyUserAuthSession(): Promise<AuthSession> {
		if (this.currentUserSession) {
			const idTokenStr = this.currentUserSession?.tokens?.idToken?.toString();
			if (!idTokenStr || this.isTokenExpired(idTokenStr)) {
				this.currentUserSession = undefined;
				this.currentUserSessionPromise = undefined;
				return this.getCurrentAmplifyUserAuthSession();
			} else {
				return Promise.resolve(this.currentUserSession);
			}
		} else if (this.currentUserSessionPromise) {
			return this.currentUserSessionPromise;
		}
		this.currentUserSessionPromise = fetchAuthSession();
		this.currentUserSessionPromise.then(authSession => this.currentUserSession = authSession);
		return this.currentUserSessionPromise;
	}

	/**
	 * Retrieve AWS credentials of current user
	 */
	updateAwsCredentials(authSession?: AuthSession) {
		AWSSDK.config.region = 'us-east-1';

		const doUpdate = (authSessionArg: AuthSession) => {
			const Logins = {};
			Logins[this.env.LOGINS_URL] = authSessionArg?.tokens?.idToken?.toString();
			AWSSDK.config.update({
				credentials: new AWSSDK.CognitoIdentityCredentials({
					IdentityPoolId: PoolData.IdentityPoolId,
					// IdentityId: authSession.identityId,
					Logins: Logins,
				}),
			});
		}

		if (authSession) {
			if (authSession) { doUpdate(authSession); }
		} else {
			this.getCurrentAmplifyUserAuthSession().then(
				authSessionRes => {
					if (authSessionRes) { doUpdate(authSessionRes); }
				},
				error => {
					console.error('getAwsCredentials().getCurrentUser():', error)
				}
			)
		}
	}

	isLoggedIn() {
		return userPool.getCurrentUser();
	}

	getUnauthCredentials(): Promise<AWSSDK.Credentials | CredentialsOptions> {
		return new Promise((resolve) => {
			const _self = this;
			AWSSDK.config.update({
				region: _self.config.cognitoConfig.region,
				credentials: new AWSSDK.CognitoIdentityCredentials({
					IdentityPoolId: PoolData.IdentityPoolId,
				}),
			});
			resolve(AWSSDK.config.credentials);
		});
	}

	updateAwsUnauthCredentials() {
		const _self = this;
		AWSSDK.config.update({
			region: _self.config.cognitoConfig.region,
			credentials: new AWSSDK.CognitoIdentityCredentials({
				IdentityPoolId: _self.config.cognitoConfig.unAuthIdentityPoolId,
			}),
		});
	}

	/**
	 * Logs out current user
	 * clears local storage and session storage
	 */
	logoutUser() {
		if (userPool.getCurrentUser() !== null) {
			userPool.getCurrentUser().signOut();
		}
		if (AWS.config.credentials && !localStorage.getItem('sso-type')) {
			AWS.config.credentials.clearCachedId();
			AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
		}
		if (window[this.config.userLoginSession.sessionCheckIntervalVarName]) {
			clearInterval(window[this.config.userLoginSession.sessionCheckIntervalVarName]);
		}
		signOut();
		localStorage.clear();
		sessionStorage.clear();
		this.dataCarrier.reset();
		this.mfaService.fetchGlobalMfaStatus();
	}

	/**
	 * Cognito forgot password
	 * @param email
	 */
	forgotPassword(email, sendLink: boolean = false, returnUrl: string = '') {
		const userData = {
			Username: email,
			Pool: userPool,
		};
		const cognitoUser = new CognitoUser(userData);
		this.result = new Subject<any>();
		let clientMetadata = {};
		const utmParams = this.getUtmParameters();
		if (sendLink && utmParams) {
			clientMetadata = {
				sendLink: sendLink.toString(),
				returnUrl: returnUrl,
				...utmParams,
			};
		} else {
			clientMetadata = { sendLink: sendLink.toString(), returnUrl: returnUrl };
		}
		cognitoUser.forgotPassword(
			{
				onSuccess: result => {
					const res = { success: true, object: result };
					this.result.next(res);
				},
				onFailure: err => {
					console.error(`[${AuthService.name}][${this.forgotPassword.name}]`, 'Error on forgot password', email, err);
					const error = { success: false, object: err };
					this.result.error(error);
				},
			},
			clientMetadata
		);
	}

	/**
	 * Cognito reset password
	 * @param email
	 */
	resetPassword(email, verificationCode, newPassword) {
		const userData = {
			Username: email,
			Pool: userPool,
		};
		const cognitoUser = new CognitoUser(userData);
		this.result = new Subject<any>();
		cognitoUser.confirmPassword(verificationCode, newPassword, {
			onSuccess: () => {
				const error = { success: true };
				this.result.next(error);
			},
			onFailure: (err: Error) => {
				console.error(`[${AuthService.name}][${this.resetPassword.name}]`, 'Error on reset password', email, err);
				const error = { success: false, object: err };
				this.result.error(error);
			},
		});
	}

	getResult(): Observable<any> {
		return this.result.asObservable()
			.pipe(
				tap(res => {
					if (res.error) {
						throw res.error;
					}
				})
			);
	}

	getSignUpResult(): Observable<any> {
		return this.signUpResult.asObservable();
	}

	/**
	 * Cognito update user password
	 * @param email
	 */
	updateUserPassword(oldPassword, newPassword) {
		const cognitoUser = userPool.getCurrentUser();
		if (cognitoUser != null) {
			// To get the session of current user
			const thisRef = this;
			cognitoUser.getSession(function (err, session) {
				if (err) {
					console.error(
						`[${AuthService.name}][${thisRef.updateUserPassword.name}][1]`,
						'Error updating user password',
						err
					);
				}
			});
		}
		this.result = new Subject<any>();
		cognitoUser.changePassword(oldPassword, newPassword, (err, res) => {
			if (err) {
				console.error(`[${AuthService.name}][${this.updateUserPassword.name}][2]`, 'Error updating user password', err);
				const error = { success: false, object: err };
				this.result.error(error);
			} else {
				const error = { success: true, object: '' };
				this.result.next(error);
			}
		});
	}

	/**
	 * Set AWS Credential for auto login using IdToken getting from SSO
	 * @param authToken - Token from SSO
	 */
	setAWSCredentials(authToken: any) {
		AWSSDK.config.region = this.config.cognitoConfig.region;
		const Logins = {};
		Logins[this.environment.getEnvironment().LOGINS_URL] = authToken.idToken.jwtToken;
		AWSSDK.config.credentials = new AWSSDK.CognitoIdentityCredentials({
			IdentityPoolId: PoolData.IdentityPoolId,
			Logins,
		});
	}

	/**
	 * Sets utm params in local storage
	 * @param params Utm Params
	 */
	setUtmParameters(params: any) {
		const utm = new UTMParameters();
		if (params.utm_source || params.utm_medium || params.utm_term || params.utm_content || params.utm_campaign) {
			utm.utmSource = params.utm_source;
			utm.utmMedium = params.utm_medium;
			utm.utmTerm = params.utm_term;
			utm.utmContent = params.utm_content;
			utm.utmCampaign = params.utm_campaign;
			localStorage.setItem('utm', JSON.stringify(utm));
		}
	}

	/**
	 * Gets Utm params object from local storage
	 */
	getUtmParameters() {
		if (localStorage.getItem('utm')) {
			const params = JSON.parse(localStorage.getItem('utm'));
			if (params) {
				const utmParams = new UtmParams();
				utmParams.utm_campaign = params.utmCampaign;
				utmParams.utm_content = params.utmContent;
				utmParams.utm_medium = params.utmMedium;
				utmParams.utm_source = params.utmSource;
				utmParams.utm_term = params.utmTerm;
				return utmParams;
			}
		}
	}
}
